import { memo } from 'react';
import { styled, Theme, OutlinedInputProps, FormHelperText, FormControl } from '@mui/material';
import MuiOutlinedInput from '@mui/material/OutlinedInput';
import { SxProps } from '@mui/system';
import { alpha } from '@mui/material/styles';

interface ICustomOutlinedInputprops extends OutlinedInputProps {
  sx?: SxProps<Theme>;
  message?: string;
}

const OutlinedInput = styled(MuiOutlinedInput)(({ theme }) => ({
  input: {
    padding: '0.53125rem 0.875rem',
    fontSize: '0.8125rem',
    lineHeight: '1.53',
    borderRadius: '0.25rem',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    height: 25,
    '&::placeholder': {
      color: theme.palette.mind.grey.placeholder,
      opacity: 1,
      transition: 'all 0.25s ease',
    },
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.light,
  },
  '&.Mui-focused': {
    boxShadow: 'none',
    '& .MuiOutlinedInput-notchedOutline': {
      boxShadow: 'none',
      border: `2px solid ${theme.palette.primary.light}`,
    },
    'input::placeholder': {
      transform: 'translate(5px)',
      transition: 'all 0.25s ease',
    },
  },
  '&.Mui-error': {
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.light,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.2)}`,
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.error.light}`,
      },
    },
  },
  '&.Mui-disabled': {
    textFillColor: 'rgba(0, 0, 0, 0.38)',
    backgroundColor: '#E0E0E0',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.26)',
    },
  },
  inputMultiline: {
    padding: 0,
  },
}));

function CustomOutlinedInput({ id, value, sx, ...props }: ICustomOutlinedInputprops) {
  const outlinedChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onChange) {
      props.onChange(event);
    }
  };
  const message = props.message || '';
  return (
    <FormControl error variant="filled" sx={{ width: '100%' }}>
      <OutlinedInput
        id={id}
        error
        value={value}
        sx={sx}
        onChange={outlinedChangeValue}
        {...props}
        aria-describedby={`${id}-helper`}
      />
      {props.error && (
        <FormHelperText id={`${id}-helper`} sx={{ marginLeft: 0 }}>
          {message}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default memo(CustomOutlinedInput);
